import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Daily-refreshed snapshot of public site counts.
 * Read from the `site_stats_snapshot` singleton table — one row, fast,
 * publicly-readable. Falls back to sensible defaults so bots and SSR
 * snapshots always see real numbers.
 *
 * Use this for:
 *  - Visible authority copy (footer stat strip, hero subtitle)
 *  - JSON-LD `numberOfItems` on the homepage
 *  - Any "X episodes indexed" string AI crawlers might quote
 */
export interface SiteStatsSnapshot {
  episodes: number;
  podcasts: number;
  topics: number;
  people: number;
  hosts: number;
  answers: number;
  lastEpisodePublishedAt: string | null;
  refreshedAt: string | null;
}

const FALLBACK: SiteStatsSnapshot = {
  episodes: 16000,
  podcasts: 58,
  topics: 90,
  people: 0,
  hosts: 0,
  answers: 0,
  lastEpisodePublishedAt: null,
  refreshedAt: null,
};

let cached: SiteStatsSnapshot | null = null;
let inflight: Promise<SiteStatsSnapshot> | null = null;

async function fetchSnapshot(): Promise<SiteStatsSnapshot> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await supabase
        .from("site_stats_snapshot")
        .select(
          "episodes_indexed, podcasts_indexed, topics_indexed, people_indexed, hosts_indexed, answers_published, last_episode_published_at, refreshed_at",
        )
        .eq("id", 1)
        .maybeSingle();
      if (!data) return FALLBACK;
      cached = {
        episodes: data.episodes_indexed ?? FALLBACK.episodes,
        podcasts: data.podcasts_indexed ?? FALLBACK.podcasts,
        topics: data.topics_indexed ?? FALLBACK.topics,
        people: data.people_indexed ?? FALLBACK.people,
        hosts: data.hosts_indexed ?? FALLBACK.hosts,
        answers: data.answers_published ?? FALLBACK.answers,
        lastEpisodePublishedAt: data.last_episode_published_at ?? null,
        refreshedAt: data.refreshed_at ?? null,
      };
      return cached;
    } catch {
      return FALLBACK;
    }
  })();
  return inflight;
}

export function useSiteStatsSnapshot(): {
  stats: SiteStatsSnapshot;
  formatted: {
    episodes: string;
    podcasts: string;
    topics: string;
    people: string;
    hosts: string;
    answers: string;
  };
} {
  const [stats, setStats] = useState<SiteStatsSnapshot>(cached ?? FALLBACK);
  useEffect(() => {
    fetchSnapshot().then((s) => {
      if (s.episodes > 0) setStats(s);
    });
    // Self-healing live count: the daily snapshot drifts behind as new
    // episodes ingest between cron runs. Fetch a live `count(*)` on
    // `episodes` + `podcasts` in parallel and prefer whichever number is
    // higher so the footer never shows a stale (lower) value than reality.
    (async () => {
      try {
        const [epRes, pcRes] = await Promise.all([
          supabase.from("episodes").select("id", { count: "exact", head: true }),
          supabase.from("podcasts").select("id", { count: "exact", head: true }),
        ]);
        const liveEpisodes = epRes.count ?? 0;
        const livePodcasts = pcRes.count ?? 0;
        setStats((prev) => {
          const nextEpisodes = Math.max(prev.episodes, liveEpisodes);
          const nextPodcasts = Math.max(prev.podcasts, livePodcasts);
          if (nextEpisodes === prev.episodes && nextPodcasts === prev.podcasts) {
            return prev;
          }
          const merged = { ...prev, episodes: nextEpisodes, podcasts: nextPodcasts };
          cached = merged;
          return merged;
        });
      } catch {
        // Silent — snapshot fallback is fine.
      }
    })();
  }, []);
  const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString("en-US");
  return {
    stats,
    formatted: {
      episodes: fmt(stats.episodes),
      podcasts: fmt(stats.podcasts),
      topics: fmt(stats.topics),
      people: fmt(stats.people),
      hosts: fmt(stats.hosts),
      answers: fmt(stats.answers),
    },
  };
}
